import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { Pencil, Trash2 } from 'lucide-react';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import { useCompany } from '../common/useCompany';
import { makeMoneyFormatter, getDisplayCurrency, convertAmount } from '../../utils/currency';

const fmtRow = (inv, value) => makeMoneyFormatter(inv.currency, { currencyDisplay: 'code' })(value);

export default function InvoiceList() {
  const { hasPermission } = useAuth();
  const company = useCompany();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const listRef = useRef(null);

  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const fmtBase = makeMoneyFormatter(baseCurrency, { currencyDisplay: 'code' });

  const load = () => {
    invoicesAPI.list({ status: status || undefined, search: search || undefined }).then(setInvoices).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };

  const doDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try {
      await invoicesAPI.remove(inv.id);
      toast.success('Invoice deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  useEffect(() => { load(); }, [status]);

  return (
    <div>
      <div className="toolbar">
        <h2>Invoices</h2>
        <div className="toolbar-right">
          <input className="form-control" placeholder="Search invoices..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} style={{width:250}} />
          <PrintButton targetRef={listRef} title={`Invoices - ${new Date().toLocaleDateString()}`} />
          {hasPermission('invoices:create') && <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>+ New Invoice</button>}
        </div>
      </div>
      <div className="filters-bar">
        {['','Draft','Sent','Partial','Paid','Overdue','Voided'].map(s => (
          <button key={s} className={`btn btn-sm ${status === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setStatus(s)}>{s || 'All'}</button>
        ))}
      </div>
      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card" ref={listRef}><div className="card-body" style={{padding:0}}>
          <PrintLetterhead />
          <table>
            <thead><tr><th>Invoice #</th><th>Customer</th><th>Date</th><th>Due Date</th><th>Currency</th><th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Due</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} style={{cursor:'pointer'}}>
                  <td className="font-mono">{inv.invoice_number}</td>
                  <td>{inv.customer_name}</td>
                  <td>{inv.invoice_date}</td>
                  <td>{inv.due_date}</td>
                  <td>{inv.currency || '—'}</td>
                  <td className="amount">
                    {fmtRow(inv, inv.total)}
                    {inv.currency && inv.currency !== baseCurrency && parseFloat(inv.total) > 0 && (
                      <div className="fx-sub">≈ {fmtBase(convertAmount(inv.total, inv.exchange_rate))}</div>
                    )}
                  </td>
                  <td className="amount">{fmtRow(inv, inv.amount_paid)}</td>
                  <td className={`amount ${parseFloat(inv.amount_due) > 0 ? 'negative' : ''}`}>{fmtRow(inv, inv.amount_due)}</td>
                  <td><span className={`badge badge-${inv.status?.toLowerCase()}`}>{inv.status}</span></td>
                  <td className="text-right" onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {hasPermission('invoices:edit') && (
                        <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => navigate(`/invoices/${inv.id}/edit`)}><Pencil size={13} /></button>
                      )}
                      {hasPermission('invoices:edit') && inv.status === 'Draft' && (
                        <button className="btn btn-ghost btn-sm" title="Delete" onClick={() => doDelete(inv)}><Trash2 size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={10} className="empty-state"><div className="empty-icon">📄</div><h3>No invoices</h3><p>Create your first invoice to start billing customers.</p></td></tr>}
            </tbody>
          </table>
        </div></div>
      )}
    </div>
  );
}
